import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { House, Plus, X, PencilSimple, CheckCircle, User as UserIcon, Phone, Envelope, CurrencyDollar } from "@phosphor-icons/react";

/**
 * UnitsEditor — AppFolio-parity Units & Leases table per property.
 * Full CRUD via a single PUT to /api/properties/:address/units (array).
 */
export default function UnitsEditor({ address }) {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/properties/${encodeURIComponent(address)}/units`)
      .then(r => setUnits(r.data.units || []))
      .catch(() => setUnits([]))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [address]);

  if (loading) return null;

  return (
    <>
      <div className="rugged-card p-4 border-l-4 border-cyan-500" data-testid="units-editor-card">
        <div className="flex items-center gap-2">
          <House weight="fill" size={18} className="text-cyan-600"/>
          <h3 className="font-display font-black text-lg uppercase">Units & Leases</h3>
          <span className="ml-2 px-2 py-0.5 bg-cyan-100 text-cyan-800 text-[10px] font-black tracking-widest uppercase rounded">
            {units.length}
          </span>
          <button onClick={() => setEditOpen(true)} data-testid="units-edit-btn"
            className="ml-auto h-8 px-3 border-2 border-slate-900 bg-white hover:bg-slate-100 text-slate-900 font-black uppercase text-[10px] tracking-widest rounded inline-flex items-center gap-1">
            <PencilSimple weight="bold" size={11}/> {units.length === 0 ? "Add" : "Edit"}
          </button>
        </div>

        {units.length === 0 ? (
          <div className="mt-3 p-4 border-2 border-dashed border-slate-300 rounded text-center text-sm text-slate-500">
            No units on file. Tap <b>Add</b> to list units, tenants, and lease terms so the WO sidebar auto-fills tenant context.
          </div>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm" data-testid="units-table">
              <thead>
                <tr className="border-b-2 border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-500">
                  <th className="text-left py-2 pr-2">Unit</th>
                  <th className="text-left py-2 pr-2">BD/BA</th>
                  <th className="text-left py-2 pr-2">Tenant</th>
                  <th className="text-left py-2 pr-2">Status</th>
                  <th className="text-right py-2 pr-2">Monthly Rent</th>
                  <th className="text-right py-2 pr-2">Lease Ends</th>
                </tr>
              </thead>
              <tbody>
                {units.map((u, i) => (
                  <tr key={i} className="border-b border-slate-100" data-testid={`unit-row-${u.unit}`}>
                    <td className="py-2 pr-2 font-black">
                      {u.unit ? <span className="px-1.5 py-0.5 bg-fuchsia-600 text-white text-[11px] rounded-sm">{u.unit}</span> : "—"}
                    </td>
                    <td className="py-2 pr-2 text-slate-600 text-[12px]">
                      {u.bd || "?"}bd / {u.ba || "?"}ba
                    </td>
                    <td className="py-2 pr-2">
                      <div className="font-bold">{u.tenant_name || <span className="text-slate-400 font-normal">Vacant</span>}</div>
                      {u.tenant_phone && <div className="text-[11px] text-slate-500">{u.tenant_phone}</div>}
                    </td>
                    <td className="py-2 pr-2">
                      <StatusChip s={u.status || (u.tenant_name ? "occupied" : "vacant")}/>
                    </td>
                    <td className="py-2 pr-2 text-right font-mono">
                      {u.monthly_rent_cents ? `$${Math.round(u.monthly_rent_cents / 100).toLocaleString()}` : "—"}
                    </td>
                    <td className="py-2 pr-2 text-right text-slate-600 text-[12px]">
                      {u.lease_end || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {editOpen && (
        <UnitsModal address={address} initial={units}
          onClose={() => setEditOpen(false)} onSaved={() => { setEditOpen(false); load(); }}/>
      )}
    </>
  );
}

function StatusChip({ s }) {
  const map = {
    occupied: "bg-emerald-100 text-emerald-800",
    vacant:   "bg-amber-100 text-amber-800",
    notice:   "bg-orange-100 text-orange-800",
    renovation: "bg-fuchsia-100 text-fuchsia-800",
  };
  const cls = map[s?.toLowerCase()] || "bg-slate-100 text-slate-700";
  return <span className={`px-2 py-0.5 text-[10px] font-black uppercase tracking-widest rounded ${cls}`}>{s || "—"}</span>;
}

function UnitsModal({ address, initial, onClose, onSaved }) {
  const [rows, setRows] = useState(() => initial.length ? [...initial] : [{ unit: "", bd: "", ba: "", tenant_name: "", tenant_phone: "", tenant_email: "", monthly_rent_cents: 0, security_deposit_cents: 0, lease_start: "", lease_end: "", status: "occupied", notes: "" }]);
  const [busy, setBusy] = useState(false);

  const setRow = (i, patch) => setRows(rs => rs.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const addRow = () => setRows(rs => [...rs, { unit: "", bd: "", ba: "", tenant_name: "", monthly_rent_cents: 0, security_deposit_cents: 0, lease_start: "", lease_end: "", status: "vacant", notes: "" }]);
  const rmRow = (i) => setRows(rs => rs.filter((_, idx) => idx !== i));

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/properties/${encodeURIComponent(address)}/units`,
        { units: rows.filter(r => (r.unit || "").trim()) });
      toast.success("Units saved");
      onSaved?.();
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start sm:items-center justify-center p-3 overflow-y-auto"
      onClick={onClose} data-testid="units-modal">
      <div className="w-full max-w-4xl bg-white rounded-lg shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b-2 border-slate-200 bg-cyan-600 text-white flex items-center gap-2">
          <House weight="fill" size={20}/>
          <div className="flex-1">
            <div className="font-display font-black text-lg uppercase leading-none">Edit Units & Leases</div>
            <div className="text-[10px] font-bold tracking-widest uppercase opacity-80 mt-0.5">{address}</div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded"><X weight="bold" size={18}/></button>
        </div>

        <div className="p-4 max-h-[70vh] overflow-y-auto space-y-3">
          {rows.map((r, i) => (
            <div key={i} className="p-3 border-2 border-slate-200 rounded-md bg-slate-50/60 space-y-2"
              data-testid={`unit-editor-${i}`}>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                <input placeholder="Unit #" value={r.unit || ""}
                  onChange={(e) => setRow(i, { unit: e.target.value })}
                  data-testid={`unit-${i}-num`}
                  className="h-9 px-2 border-2 border-slate-300 rounded text-sm font-black focus:border-cyan-500 focus:outline-none"/>
                <input placeholder="BD" type="number" step="0.5" value={r.bd || ""}
                  onChange={(e) => setRow(i, { bd: e.target.value })}
                  className="h-9 px-2 border-2 border-slate-300 rounded text-sm focus:border-cyan-500 focus:outline-none"/>
                <input placeholder="BA" type="number" step="0.5" value={r.ba || ""}
                  onChange={(e) => setRow(i, { ba: e.target.value })}
                  className="h-9 px-2 border-2 border-slate-300 rounded text-sm focus:border-cyan-500 focus:outline-none"/>
                <select value={r.status || "occupied"} onChange={(e) => setRow(i, { status: e.target.value })}
                  className="h-9 px-2 border-2 border-slate-300 rounded text-sm focus:border-cyan-500 focus:outline-none">
                  <option value="occupied">Occupied</option>
                  <option value="vacant">Vacant</option>
                  <option value="notice">Notice Given</option>
                  <option value="renovation">Renovation</option>
                </select>
                <div className="col-span-2 flex items-center gap-1">
                  <span className="text-slate-500 font-black">$</span>
                  <input placeholder="Rent" type="number" step="10"
                    value={Math.round((r.monthly_rent_cents || 0) / 100) || ""}
                    onChange={(e) => setRow(i, { monthly_rent_cents: Math.max(0, parseInt(e.target.value || "0", 10)) * 100 })}
                    data-testid={`unit-${i}-rent`}
                    className="w-full h-9 px-2 border-2 border-slate-300 rounded text-sm font-mono focus:border-cyan-500 focus:outline-none"/>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <input placeholder="Tenant name" value={r.tenant_name || ""}
                  onChange={(e) => setRow(i, { tenant_name: e.target.value })}
                  data-testid={`unit-${i}-tenant`}
                  className="h-9 px-2 border-2 border-slate-300 rounded text-sm focus:border-cyan-500 focus:outline-none"/>
                <input placeholder="Tenant phone" value={r.tenant_phone || ""}
                  onChange={(e) => setRow(i, { tenant_phone: e.target.value })}
                  className="h-9 px-2 border-2 border-slate-300 rounded text-sm focus:border-cyan-500 focus:outline-none"/>
                <input placeholder="Lease start" value={r.lease_start || ""}
                  onChange={(e) => setRow(i, { lease_start: e.target.value })}
                  className="h-9 px-2 border-2 border-slate-300 rounded text-sm focus:border-cyan-500 focus:outline-none"/>
                <input placeholder="Lease end" value={r.lease_end || ""}
                  onChange={(e) => setRow(i, { lease_end: e.target.value })}
                  className="h-9 px-2 border-2 border-slate-300 rounded text-sm focus:border-cyan-500 focus:outline-none"/>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                <input placeholder="Tenant email" value={r.tenant_email || ""}
                  onChange={(e) => setRow(i, { tenant_email: e.target.value })}
                  className="h-9 px-2 border-2 border-slate-300 rounded text-sm focus:border-cyan-500 focus:outline-none"/>
                <div className="flex items-center gap-1">
                  <span className="text-slate-500 font-black text-xs">Deposit $</span>
                  <input type="number" step="10"
                    value={Math.round((r.security_deposit_cents || 0) / 100) || ""}
                    onChange={(e) => setRow(i, { security_deposit_cents: Math.max(0, parseInt(e.target.value || "0", 10)) * 100 })}
                    className="w-full h-9 px-2 border-2 border-slate-300 rounded text-sm font-mono focus:border-cyan-500 focus:outline-none"/>
                </div>
                <div className="flex justify-end">
                  <button onClick={() => rmRow(i)} data-testid={`unit-${i}-delete`}
                    className="h-9 px-3 border-2 border-red-500 bg-white text-red-600 hover:bg-red-50 font-black uppercase text-[10px] tracking-widest rounded">
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
          <button onClick={addRow} data-testid="units-add-row"
            className="w-full h-11 border-2 border-dashed border-cyan-400 text-cyan-700 hover:bg-cyan-50 font-black uppercase text-xs tracking-widest rounded inline-flex items-center justify-center gap-1">
            <Plus weight="bold" size={13}/> Add another unit
          </button>
        </div>

        <div className="p-3 border-t-2 border-slate-200 flex gap-2 justify-end bg-slate-50">
          <button onClick={onClose}
            className="h-10 px-4 border-2 border-slate-300 bg-white text-slate-700 font-black uppercase text-xs tracking-widest rounded">
            Cancel
          </button>
          <button onClick={save} disabled={busy} data-testid="units-save-btn"
            className="h-10 px-5 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white font-black uppercase text-xs tracking-widest rounded inline-flex items-center gap-1.5 shadow-[3px_3px_0_#0f172a]">
            <CheckCircle weight="fill" size={13}/>
            {busy ? "Saving…" : `Save ${rows.filter(r => (r.unit || "").trim()).length} units`}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Compact lease/tenant chip for the WO sidebar — pulls the matching unit
 *  from property_configs by address+unit. Hides entirely if no match. */
export function WoLeaseContextChip({ address, unit }) {
  const [u, setU] = useState(null);
  useEffect(() => {
    if (!address || !unit) return;
    api.get(`/properties/lookup-unit?address=${encodeURIComponent(address)}&unit=${encodeURIComponent(unit)}`)
      .then(r => setU(r.data.unit || null))
      .catch(() => setU(null));
  }, [address, unit]);

  if (!u) return null;

  return (
    <div className="mt-4 border-2 border-slate-300 bg-white shadow-[3px_3px_0_#0f172a] rounded-sm"
      data-testid="wo-lease-context-chip">
      <div className="px-3 py-2 bg-slate-100 border-b-2 border-slate-200 flex items-center gap-2">
        <UserIcon weight="fill" size={14} className="text-cyan-600"/>
        <div className="font-display font-black uppercase tracking-wider text-[11px] text-slate-800">
          Lease Context
        </div>
      </div>
      <div className="p-3 space-y-2 text-[12px]">
        <div>
          <div className="font-black text-slate-900">{u.tenant_name || "Vacant unit"}</div>
          {u.tenant_phone && (
            <div className="text-[11px] text-slate-500 inline-flex items-center gap-1">
              <Phone weight="bold" size={10}/> {u.tenant_phone}
            </div>
          )}
          {u.tenant_email && (
            <div className="text-[11px] text-slate-500 inline-flex items-center gap-1">
              <Envelope weight="bold" size={10}/> {u.tenant_email}
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2 pt-1 border-t border-slate-100">
          <div>
            <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Monthly Rent</div>
            <div className="font-mono font-black">
              {u.monthly_rent_cents ? `$${Math.round(u.monthly_rent_cents / 100).toLocaleString()}` : "—"}
            </div>
          </div>
          <div>
            <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Deposit</div>
            <div className="font-mono font-black">
              {u.security_deposit_cents ? `$${Math.round(u.security_deposit_cents / 100).toLocaleString()}` : "—"}
            </div>
          </div>
          {u.lease_start && (
            <div>
              <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Lease Start</div>
              <div className="text-[11px] font-bold">{u.lease_start}</div>
            </div>
          )}
          {u.lease_end && (
            <div>
              <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Lease End</div>
              <div className="text-[11px] font-bold">{u.lease_end}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
